/**
 * ForFoundersPage — the public /for/founders archetype landing page.
 *
 * Ported verbatim from the locked design design/mockups/V1_Landing_For_Founders_v1.html
 * (Template 2 instance, source V1_Archetype_Landing_Pages.md Page 4). Replaces the
 * noindex marketing scaffold for /for/founders with real, indexable content.
 *
 * The app shell's <TopNav> renders the nav; this page is content + footer
 * (everything below the mockup's <nav class="marketing-nav">). Scoped <style>
 * block aliases the mockup's colors onto locked --ppr-* tokens; replicates the
 * locked pattern from PricingPage.jsx.
 */

import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const FOUNDERS_DESCRIPTION =
  "PeoplePrints gives founders and small-business owners the candidate read an HR team would give — without the HR team. No assessment, no test. From $49/month.";

const FAQS = [
  {
    q: "I've never run a structured interview. Is this usable for me?",
    a: "Yes — it's built for exactly this. The Interview Blueprint is in plain language and tells you what to do step by step. No HR background required.",
  },
  {
    q: "Do candidates have to take a test?",
    a: "No. The Profile is generated from minimal common data points. Candidates do nothing.",
  },
  {
    q: "Is $49/month really enough for a small business?",
    a: "The Recruiter tier at $49/month covers 25 Profiles — enough for a steady small-business hiring cadence. There's no per-candidate fee and no setup cost.",
  },
  {
    q: "What happens after I hire someone?",
    a: "The Operating Manual Blueprint gives you the read on how to manage the new hire — how they decide, what motivates them, how to give them feedback. On the Manager tier you also get Performance Review and Relational Blueprints.",
  },
  {
    q: "Is there a contract?",
    a: "No. Both tiers are month-to-month. Cancel anytime.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). Mirrors the locked pattern in PricingPage.jsx.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function ForFoundersPage() {
  usePageMeta({
    title:
      "No HR Department? Every Hire Still Has to Be Right — PeoplePrints for Founders",
    description: FOUNDERS_DESCRIPTION,
    canonicalPath: "/for/founders",
  });

  return (
    <div className="ppr-founders-root">
      <style>{FOUNDERS_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── BLOCK 2 — HERO ── */}
      <section className="ppr-founders-hero">
        <div className="ppr-founders-hero-inner">
          <p className="ppr-founders-hero-eyebrow">
            For founders &amp; small-business owners
          </p>
          <h1 className="ppr-founders-hero-h1">
            No HR department? Every hire still has to be right.
          </h1>
          <p className="ppr-founders-hero-sub">
            When the team is small, every hire is high-stakes — a wrong one
            costs more than a salary. You don't have an HR team to read
            candidates for you, and you don't have time to become an interview
            expert. PeoplePrints gives you the read an HR team would give,
            without the HR team.
          </p>
        </div>
      </section>

      {/* ── BLOCK 3 — EXTRACTABLE DEFINITION ── */}
      <section className="ppr-founders-block ppr-founders-compact">
        <div className="ppr-founders-block-inner">
          <div className="ppr-founders-definition">
            <p className="ppr-founders-def-label">
              What PeoplePrints does for founders
            </p>
            <p className="ppr-founders-def-text">
              PeoplePrints is a pre-interaction intelligence tool for founders
              and small-business owners hiring without an HR department. It
              generates a <strong>Predisposition Profile</strong> for each
              candidate from minimal common data points — no assessment
              required — and converts it into an Interview Blueprint that tells
              the founder how the candidate tends to make decisions and how to
              structure the interview. After the hire, the Operating Manual
              Blueprint helps the founder manage the new team member.
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 4 — THE PAIN ── */}
      <section className="ppr-founders-block ppr-founders-compact">
        <div className="ppr-founders-block-inner">
          <p className="ppr-founders-block-eyebrow">Sound familiar?</p>
          <h2 className="ppr-founders-block-h2">
            A bad hire on a five-person team is 20% of the company.
          </h2>
          <p className="ppr-founders-pain-quote">
            You wear every hat. Hiring is one of them, and it's the one where a
            mistake compounds — a bad hire on a five-person team is 20% of the
            company. You've read the résumés. You've done the calls. But you're
            guessing on the read, because reading candidates was never the
            thing you were trained to do.
          </p>
        </div>
      </section>

      {/* ── BLOCK 5 — THE SHIFT ── */}
      <section className="ppr-founders-block ppr-founders-compact ppr-founders-tinted">
        <div className="ppr-founders-block-inner">
          <p className="ppr-founders-block-eyebrow">The shift</p>
          <h2 className="ppr-founders-block-h2">Stop guessing on the read.</h2>
          <p className="ppr-founders-block-body">
            With PeoplePrints, you stop guessing. The Interview Blueprint tells
            you what to ask, what to watch for, and how this specific candidate
            tends to make decisions — before the call. After you hire, the
            Operating Manual gives you the read on how to manage them. It's the
            HR function you can't yet afford, in a tool that costs less than
            lunch for the team.
          </p>
        </div>
      </section>

      {/* ── BLOCK 6 — WHAT YOU GET (In-This-Blueprint card grammar) ── */}
      <section className="ppr-founders-block ppr-founders-compact">
        <div className="ppr-founders-block-inner">
          <h2 className="ppr-founders-block-h2">
            The Blueprints that matter to a founder.
          </h2>
          <div className="ppr-founders-itb-card">
            <div className="ppr-founders-itb-stripe"></div>
            <div className="ppr-founders-itb-inner">
              <p className="ppr-founders-itb-label">What you get</p>
              <ol className="ppr-founders-itb-list">
                <li>
                  <strong>A Predisposition Profile for every candidate</strong>{" "}
                  — no assessment, no friction.
                </li>
                <li>
                  <strong>The Interview Blueprint</strong> — walk into the
                  hiring conversation prepared.
                </li>
                <li>
                  <strong>The Operating Manual Blueprint</strong> — after the
                  hire, the read on how to manage the new team member.
                </li>
                <li>
                  <strong>No HR expertise required</strong> — the Blueprint is
                  in plain language, built for someone who has never run a
                  structured interview.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 7 — HOW IT WORKS ── */}
      <section className="ppr-founders-block ppr-founders-compact ppr-founders-tinted">
        <div className="ppr-founders-block-inner">
          <p className="ppr-founders-block-eyebrow">How it works</p>
          <h2 className="ppr-founders-block-h2">
            Minimal data in. Profile. Blueprint.
          </h2>
          <div className="ppr-founders-step-list">
            <div className="ppr-founders-step-row">
              <div className="ppr-founders-step-num">1</div>
              <p className="ppr-founders-step-text">
                <strong>Enter the candidate's common data points.</strong>
              </p>
            </div>
            <div className="ppr-founders-step-row">
              <div className="ppr-founders-step-num">2</div>
              <p className="ppr-founders-step-text">
                <strong>
                  The engine generates the Predisposition Profile.
                </strong>
              </p>
            </div>
            <div className="ppr-founders-step-row">
              <div className="ppr-founders-step-num">3</div>
              <p className="ppr-founders-step-text">
                <strong>
                  Open the Interview Blueprint — then the Operating Manual
                  after you hire.
                </strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 8 — PRICING POINTER ── */}
      <section className="ppr-founders-block ppr-founders-compact">
        <div className="ppr-founders-block-inner">
          <div className="ppr-founders-price-pointer">
            <p className="ppr-founders-pp-tier">Recruiter — $49 / month</p>
            <p className="ppr-founders-pp-body">
              Start on the Recruiter tier at <strong>$49/month</strong> — 25
              Profiles, Interview Blueprints, everything you need to hire well.
              As your team grows and you're managing more than hiring, the
              Manager tier at $149/month adds the Performance Review, Operating
              Manual, and Relational Blueprints. Both are month-to-month.
            </p>
          </div>
          <p className="ppr-founders-xlink">
            Team growing past your first few hires? See{" "}
            <Link to="/for/managers">PeoplePrints for managers →</Link>
          </p>
        </div>
      </section>

      {/* ── BLOCK 9 — FAQ ── */}
      <section className="ppr-founders-block ppr-founders-compact ppr-founders-tinted">
        <div className="ppr-founders-block-inner">
          <p className="ppr-founders-block-eyebrow">FAQ</p>
          <h2 className="ppr-founders-block-h2">Questions founders ask.</h2>
          <div className="ppr-founders-faq-list">
            {FAQS.map((f, i) => (
              <details
                className="ppr-founders-faq-item"
                key={f.q}
                open={i === 0}
              >
                <summary className="ppr-founders-faq-q">{f.q}</summary>
                <p className="ppr-founders-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 10 — CREDENTIALING STRIP ── */}
      <section className="ppr-founders-cred-strip">
        <div className="ppr-founders-cred-strip-inner">
          <span className="ppr-founders-cs-label">The engine</span>
          PeoplePrints is built on a proprietary behavioral-prediction engine.
          The full methodology is at <Link to="/methodology">/methodology</Link>
          .
        </div>
      </section>

      {/* ── BLOCK 11 — CTA ── */}
      <section className="ppr-founders-cta-band">
        <div className="ppr-founders-cta-band-inner">
          <h2 className="ppr-founders-cta-band-h">
            Get the read an HR team would give — without the HR team.
          </h2>
          <div className="ppr-founders-cta-actions">
            <Link
              to="/demo"
              className="ppr-founders-cta-btn ppr-founders-cta-primary"
            >
              Request a demo
            </Link>
            <Link
              to="/demo"
              className="ppr-founders-cta-btn ppr-founders-cta-secondary"
            >
              Book a 30-minute demo
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer — clean A6 nav ── */}
      <footer className="ppr-founders-footer">
        <div className="ppr-founders-footer-inner">
          <div className="ppr-founders-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppr-founders-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppr-founders-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const FOUNDERS_CSS = `
.ppr-founders-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel: var(--ppr-color-accent-steel);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale: var(--ppr-color-accent-pale);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  --c-coral-very-soft: var(--ppr-color-accent-coral-very-soft);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design (same flag as PricingPage / the C1 homepage rebuild). */
  --c-gold: #C99A4B;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppr-founders-root * { box-sizing: border-box; }

.ppr-founders-block { padding: 64px 32px; }
.ppr-founders-block.ppr-founders-compact { padding: 44px 32px; }
.ppr-founders-block.ppr-founders-tinted { background: var(--c-pale-soft); }
.ppr-founders-block-inner { max-width: 940px; margin: 0 auto; }
.ppr-founders-block-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppr-founders-block-h2 { font-family: var(--f-display); font-size: 27px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 16px; }
.ppr-founders-block-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); margin: 0 0 16px; }
.ppr-founders-block-body strong { color: var(--c-navy); }

.ppr-founders-hero { padding: 80px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppr-founders-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppr-founders-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppr-founders-hero-h1 { font-family: var(--f-display); font-size: 40px; line-height: 1.1; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 20px; }
.ppr-founders-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; text-align: left; }

.ppr-founders-definition { background: var(--c-pale-soft); border: 1.5px solid var(--c-powder); border-left: 6px solid var(--c-navy); padding: 26px 30px; }
.ppr-founders-def-label { font-family: var(--f-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-steel-dark); margin: 0 0 10px; }
.ppr-founders-def-text { font-family: var(--f-body); font-size: 17px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-founders-def-text strong { color: var(--c-navy); }

.ppr-founders-pain-quote { font-family: var(--f-body); font-size: 17px; font-style: italic; line-height: 1.6; color: var(--c-text); border-left: 4px solid var(--c-coral); background: var(--c-coral-very-soft); padding: 20px 24px; margin: 0; }

.ppr-founders-itb-card { background: var(--c-surface); border: 3px solid var(--c-navy); margin: 6px 0 0; }
.ppr-founders-itb-stripe { height: 8px; background: var(--c-coral); }
.ppr-founders-itb-inner { padding: 24px 28px; }
.ppr-founders-itb-label { font-family: var(--f-display); font-size: 19px; color: var(--c-navy); text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 14px; }
.ppr-founders-itb-list { margin: 0; padding: 0; list-style: none; counter-reset: itb; }
.ppr-founders-itb-list li { position: relative; padding: 11px 0 11px 38px; font-family: var(--f-body); font-size: 15.5px; line-height: 1.55; border-bottom: 1px solid var(--c-border); counter-increment: itb; }
.ppr-founders-itb-list li:last-child { border-bottom: none; padding-bottom: 0; }
.ppr-founders-itb-list li::before { content: counter(itb); position: absolute; left: 0; top: 10px; width: 23px; height: 23px; background: var(--c-navy); color: #FFF; border-radius: 50%; font-family: var(--f-display); font-size: 11px; display: flex; align-items: center; justify-content: center; }
.ppr-founders-itb-list strong { color: var(--c-navy); }

.ppr-founders-step-list { margin: 20px 0 0; }
.ppr-founders-step-row { display: grid; grid-template-columns: 40px 1fr; gap: 18px; padding: 15px 0; border-bottom: 1px solid var(--c-border); }
.ppr-founders-step-row:last-child { border-bottom: none; }
.ppr-founders-step-num { width: 32px; height: 32px; border-radius: 50%; background: var(--c-navy); color: #FFF; font-family: var(--f-display); font-size: 13px; display: flex; align-items: center; justify-content: center; }
.ppr-founders-step-text { font-family: var(--f-body); font-size: 15.5px; line-height: 1.6; color: var(--c-text); margin: 4px 0 0; }
.ppr-founders-step-text strong { color: var(--c-navy); }

.ppr-founders-price-pointer { border: 2px solid var(--c-navy); border-top: 8px solid var(--c-coral); background: var(--c-surface); padding: 26px 30px; }
.ppr-founders-pp-tier { font-family: var(--f-display); font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 8px; }
.ppr-founders-pp-body { font-family: var(--f-body); font-size: 16px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-founders-pp-body strong { color: var(--c-navy); }
.ppr-founders-xlink { font-family: var(--f-ui); font-size: 14px; color: var(--c-text-2); margin: 18px 0 0; }
.ppr-founders-xlink a { color: var(--c-coral-dark); font-weight: 600; text-decoration: none; }

.ppr-founders-faq-list { margin: 20px 0 0; }
.ppr-founders-faq-item { border: 1.5px solid var(--c-border); border-left: 4px solid var(--c-steel); margin: 0 0 12px; background: var(--c-surface); padding: 20px 24px; }
.ppr-founders-faq-q { font-family: var(--f-display); font-size: 15.5px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.005em; cursor: pointer; list-style: none; }
.ppr-founders-faq-q::-webkit-details-marker { display: none; }
.ppr-founders-faq-item[open] .ppr-founders-faq-q { margin-bottom: 8px; }
.ppr-founders-faq-a { font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-founders-faq-a strong { color: var(--c-navy); }

.ppr-founders-cred-strip { background: var(--c-navy); color: #FFF; padding: 28px 32px; }
.ppr-founders-cred-strip-inner { max-width: 940px; margin: 0 auto; font-family: var(--f-body); font-size: 14.5px; line-height: 1.65; }
.ppr-founders-cred-strip-inner a { color: var(--c-powder); text-decoration: underline; }
.ppr-founders-cs-label { font-family: var(--f-display); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-powder); display: block; margin: 0 0 8px; }

.ppr-founders-cta-band { background: var(--c-pale-soft); padding: 60px 32px; text-align: center; }
.ppr-founders-cta-band-inner { max-width: 760px; margin: 0 auto; }
.ppr-founders-cta-band-h { font-family: var(--f-display); font-size: 24px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 24px; }
.ppr-founders-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.ppr-founders-cta-btn { display: inline-block; padding: 14px 26px; border-radius: 6px; text-decoration: none; font-family: var(--f-display); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.ppr-founders-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppr-founders-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppr-founders-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppr-founders-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppr-founders-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppr-founders-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppr-founders-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppr-founders-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppr-founders-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 800px) {
  .ppr-founders-hero-h1 { font-size: 30px; }
  .ppr-founders-block-h2 { font-size: 22px; }
}
`;
